import { useId, useState, type FormEvent } from "react";
import { useShallow } from "zustand/react/shallow";
import { PERSON_SEARCH_MIN_CHARS, worldCopyName, type PersonSearchResult, type WorldCopyView } from "@townsy/contracts";
import { errorMessage } from "../../api/client";
import { usePersonSearchQuery, useWorldsQuery } from "../../api/worlds";
import { useTownSession } from "../../app/TownSessionContext";
import { useTownStore, type WorldSwitchUi } from "../../state/store";
import { Dialog } from "../Dialog";
import { FlagIcon } from "../FlagIcon";

/**
 * Named world copies (ADR-0043 decisions 5-6). The HUD's world chip ("Wren · 42 people") opens this dialog: every open
 * copy of the town with its people (full ones marked), a Switch button (pending, refusal and error states; the server
 * decides and the visitor stays put on a refusal), and "Find a person" (the server shows only people whose setting lets
 * you see their world). Everything is a real button or input; Escape and the backdrop close it; on a phone it fills the
 * screen (styles: `.worlds-*`).
 */

/** "Wren · 42 people" (the HUD chip; one person reads "1 person"). */
export function worldChipLabel(copy: number, people: number | null): string {
  const name = worldCopyName(copy);
  if (people === null) return name;
  return `${name} · ${people} ${people === 1 ? "person" : "people"}`;
}

/** The HUD chip: the copy and its people (street), or the copy the visitor came from (inside); opens the Worlds dialog. */
export function WorldChip() {
  const { copy, people, kind, joined } = useTownStore(
    useShallow((s) => ({ copy: s.room.worldCopy, people: s.room.peopleCount, kind: s.room.kind, joined: s.room.status === "joined" })),
  );
  const open = useTownStore((s) => s.ui.worldsOpen);
  const session = useTownSession();
  if (copy === null || !session) return null;
  const label = kind === "street" ? worldChipLabel(copy, joined ? people : null) : worldCopyName(copy);
  return (
    <button
      type="button"
      className="btn btn-sm world-chip"
      aria-pressed={open}
      aria-haspopup="dialog"
      onClick={() => session.setWorldsOpen(!open)}
      title={kind === "street" ? "The copy of the town you're in, and its people. Choose another world or find a friend." : "The world you came from (you go back to it when you leave). Choose another world or find a friend."}
      data-testid="hud-world"
    >
      <span aria-hidden="true">🌍 </span>
      {label}
    </button>
  );
}

/** Mounted once in the town: the dialog while it is open. */
export function WorldsHost() {
  const open = useTownStore((s) => s.ui.worldsOpen);
  const session = useTownSession();
  if (!open || !session) return null;
  return <WorldsDialog onClose={() => session.setWorldsOpen(false)} />;
}

function WorldsDialog({ onClose }: { onClose(): void }) {
  const session = useTownSession();
  const worlds = useWorldsQuery(true);
  const { myCopy, kind, joined, switching } = useTownStore(
    useShallow((s) => ({
      myCopy: s.room.worldCopy,
      kind: s.room.kind,
      joined: s.room.status === "joined" && !s.ui.transitionPending,
      switching: s.ui.worldSwitch,
    })),
  );
  const [query, setQuery] = useState<string | null>(null);
  const search = usePersonSearchQuery(query);
  // The server never lists someone an account muted (either way); a guest's mutes live in this browser, so they are
  // left out here too.
  const mutes = useTownStore((s) => s.mutes);
  const results = search.data ? search.data.items.filter((r) => !(r.actorId in mutes)) : null;
  return (
    <WorldsDialogView
      onClose={onClose}
      copies={worlds.data?.copies ?? null}
      loadError={worlds.error ? errorMessage(worlds.error, "The list of worlds could not be loaded.") : null}
      myCopy={myCopy}
      inside={kind === "interior"}
      canSwitch={joined && Boolean(session)}
      switching={switching}
      onSwitch={(copy) => session?.switchWorld(copy)}
      onSearch={(q) => setQuery(q)}
      searched={query}
      results={results}
      searchPending={Boolean(query) && search.isPending}
      searchError={search.error ? errorMessage(search.error, "The search did not work. Try again.") : null}
      onJoin={(actorId) => session?.joinFriend(actorId)}
    />
  );
}

export interface WorldsDialogViewProps {
  onClose(): void;
  /** The open copies (null while loading). */
  copies: WorldCopyView[] | null;
  loadError: string | null;
  /** The visitor's copy (inside a building: the copy they came from). */
  myCopy: number | null;
  inside: boolean;
  /** In a room and not moving: switching is possible. */
  canSwitch: boolean;
  switching: WorldSwitchUi;
  onSwitch(copy: number): void;
  onSearch(q: string): void;
  searched: string | null;
  results: PersonSearchResult[] | null;
  searchPending: boolean;
  searchError: string | null;
  onJoin(actorId: string): void;
}

/** The Worlds dialog, presentational (server-rendered in the unit tests). */
export function WorldsDialogView(p: WorldsDialogViewProps) {
  const listId = useId();
  const pendingCopy = p.switching.status === "pending" ? p.switching.copy : null;
  const busy = p.switching.status === "pending";
  return (
    <Dialog title="Worlds" onClose={p.onClose} describedBy={listId} className="worlds-dialog">
      <p className="small" id={listId} style={{ marginTop: 0 }}>
        Townsy runs several copies of the same town when it is busy. Shops, signs, owners, offers, coins and homes are the same in every
        world; each world has its own crowd, street chat and traffic. People from every world meet inside buildings.
        {p.inside ? " Switching from inside a building takes you out of its door, in the world you choose." : " Switching keeps your spot in town."}
      </p>
      {p.switching.status === "refused" ? (
        <div className="notice notice-warning small" role="alert" data-testid="worlds-refusal">
          {p.switching.message}
        </div>
      ) : null}
      {busy ? (
        <p className="small" role="status" data-testid="worlds-pending">
          <span className="msgr-spinner" aria-hidden="true" /> {pendingCopy !== null ? `Moving you to ${worldCopyName(pendingCopy)}…` : "Finding their world…"}
        </p>
      ) : null}
      {p.loadError ? (
        <div className="notice notice-info small" role="alert" data-testid="worlds-error">
          {p.loadError}
        </div>
      ) : null}
      {p.copies === null && !p.loadError ? <p className="small muted">Loading the worlds…</p> : null}
      {p.copies ? (
        <ul className="worlds-list" aria-label="Open worlds" data-testid="worlds-list">
          {p.copies.map((c) => {
            const here = c.copy === p.myCopy;
            const pendingHere = pendingCopy === c.copy;
            const disabledReason = here ? (p.inside ? "You came from this world" : "You're here") : c.full ? "Full" : !p.canSwitch ? "Not now" : null;
            return (
              <li key={c.copy} className={`worlds-row${here ? " is-here" : ""}${c.full ? " is-full" : ""}`} data-testid={`world-row-${c.copy}`} data-full={c.full ? "true" : "false"}>
                <span className="worlds-name">{c.name}</span>
                <span className="worlds-people">
                  {c.people} {c.people === 1 ? "person" : "people"}
                </span>
                {c.full ? <span className="badge badge-warning">Full</span> : null}
                {here ? <span className="badge">{p.inside ? "Your world" : "You're here"}</span> : null}
                <span className="spacer" />
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={Boolean(disabledReason) || busy}
                  title={disabledReason ?? `Go to ${c.name}`}
                  aria-label={pendingHere ? `Switching to ${c.name}` : `Switch to ${c.name}, ${c.people} ${c.people === 1 ? "person" : "people"}${c.full ? ", full" : ""}`}
                  onClick={() => p.onSwitch(c.copy)}
                  data-testid={`world-switch-${c.copy}`}
                >
                  {pendingHere ? "Switching…" : here ? "Here" : c.full ? "Full" : "Switch"}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
      <FindPerson {...p} busy={busy} />
    </Dialog>
  );
}

/** "Find a person": display name or its beginning; only people whose setting lets you see their world are listed. */
function FindPerson(p: WorldsDialogViewProps & { busy: boolean }) {
  const inputId = useId();
  const hintId = useId();
  const [text, setText] = useState(p.searched ?? "");
  const trimmed = text.trim();
  const valid = /^[A-Za-z0-9_]{3,20}$/.test(trimmed);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (valid) p.onSearch(trimmed);
  };
  return (
    <section className="worlds-find" aria-labelledby={`${inputId}-title`} data-testid="find-person">
      <h3 id={`${inputId}-title`} className="section-title">
        Find a person
      </h3>
      <form className="row" onSubmit={submit} role="search">
        <label htmlFor={inputId} className="visually-hidden">
          Display name
        </label>
        <input
          id={inputId}
          className="input"
          type="search"
          value={text}
          maxLength={20}
          autoComplete="off"
          spellCheck={false}
          placeholder="Display name"
          aria-describedby={hintId}
          onChange={(e) => setText(e.target.value)}
          data-testid="find-person-input"
        />
        <button type="submit" className="btn btn-sm" disabled={!valid || p.searchPending} data-testid="find-person-submit">
          {p.searchPending ? "Searching…" : "Search"}
        </button>
      </form>
      <p className="hint" id={hintId} style={{ margin: 0 }}>
        At least {PERSON_SEARCH_MIN_CHARS} letters or digits of their display name. You see someone only while they are online and their setting lets you see
        which world they're in (most people show it to the people they've messaged).
      </p>
      {p.searchError ? (
        <div className="small error-text" role="alert" data-testid="find-person-error">
          {p.searchError}
        </div>
      ) : null}
      {p.results && p.searched ? (
        p.results.length === 0 ? (
          <p className="small muted" role="status" data-testid="find-person-empty">
            Nobody found for “{p.searched}”.
          </p>
        ) : (
          <ul className="worlds-list" aria-label="People found" data-testid="find-person-results">
            {p.results.map((r) => {
              const same = r.world.copy === p.myCopy && !p.inside;
              return (
                <li key={r.actorId} className="worlds-row" data-testid="find-person-row" data-actor-id={r.actorId}>
                  <span className="worlds-name">
                    <FlagIcon code={r.countryCode} /> {r.label}
                  </span>
                  <span className="worlds-people">in {r.world.name}</span>
                  <span className="spacer" />
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    disabled={same || !p.canSwitch || p.busy}
                    title={same ? "They're in your world" : `Go to ${r.world.name}`}
                    onClick={() => p.onJoin(r.actorId)}
                    data-testid="find-person-join"
                  >
                    {same ? "In your world" : `Join them in ${r.world.name}`}
                  </button>
                </li>
              );
            })}
          </ul>
        )
      ) : null}
    </section>
  );
}
